import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lightbulb, Users, CheckCircle, Play, FileText, Target, TrendingUp, MessageCircle } from 'lucide-react';

// Função para criar links do WhatsApp com mensagens contextuais
const createWhatsAppLink = (context: string = 'geral') => {
  const phoneNumber = '5531972061387';
  const messages = {
    'geral': 'Olá! Gostaria de saber mais sobre a plataforma Suh Freitas.',
    'suporte': 'Olá! Preciso de ajuda com a plataforma Suh Freitas.',
    'premium': 'Olá! Tenho interesse nos planos premium da Suh Freitas.',
    'mentoria': 'Olá! Gostaria de saber mais sobre as mentorias com a Suh Freitas.',
    'assessment': 'Olá! Tenho dúvidas sobre o teste de perfil de comunicação.',
    'home': 'Olá! Vi seu site e gostaria de saber mais sobre como funciona o teste de perfil de comunicação.',
  };
  
  const message = messages[context as keyof typeof messages] || messages.geral;
  return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
};
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ProfileIcon } from '../components/ui/ProfileIcon';
import { useAuthStore } from '../store/authStore';
import { useProfileStore } from '../store/profileStore';
import { getEstimatedTime } from '../config/assessment';

// A interface 'Profile' local e a constante 'PROFILES' foram removidas.

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { profiles, loading, error, fetchProfiles } = useProfileStore();

  useEffect(() => {
    fetchProfiles();
  }, []); // Remove fetchProfiles da dependência para evitar loops


  const handleStartAssessment = () => {
    if (isAuthenticated) {
      navigate('/assessment');
    } else {
      navigate('/login');
    }
  };

  // A função getProfileIcon foi removida pois os ícones não fazem parte do novo tipo Profile.
  // A lógica de ícones precisará ser reimplementada se necessário.

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-neutral-50/30">
      {/* Hero Section - Following Wireframe */}
      <section className="pt-20 pb-28 px-4 relative overflow-hidden bg-gradient-to-br from-white via-primary-50/30 to-secondary-50/20">
        {/* Enhanced decorative background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/40 to-secondary-50/30"></div>
        <div className="absolute top-20 right-10 w-40 h-40 bg-gradient-to-br from-primary-200/30 to-secondary-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-gradient-to-br from-secondary-200/25 to-primary-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-neutral-100/20 to-primary-100/20 rounded-full blur-3xl opacity-60"></div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 mb-6 leading-tight tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            DESCUBRA SEU <br/>
            <motion.span 
              className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent font-extrabold"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            >
              PERFIL DE COMUNICAÇÃO
            </motion.span>
            <br />
            <span className="text-3xl md:text-4xl lg:text-5xl text-neutral-700 font-medium">
              EM APENAS {getEstimatedTime()} MINUTOS
            </span>
          </motion.h1>
          
          <motion.div 
            className="space-y-5 mb-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            <p className="text-xl text-neutral-700 font-medium leading-relaxed">
              Metodologia científica baseada em 8 perfis únicos
            </p>
          </motion.div>

          <motion.div 
            className="flex flex-col sm:flex-row gap-5 justify-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
          >
            <Button 
              variant="primary"
              size="lg" 
              onClick={handleStartAssessment}
              className="text-lg px-12 py-4 rounded-2xl shadow-elegant hover:shadow-premium transition-all duration-300 transform hover:-translate-y-1"
            >
              Começar Teste Grátis
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => {
                const howItWorksSection = document.getElementById('como-funciona');
                howItWorksSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-lg px-12 py-4 rounded-2xl border-2 border-primary-300 text-primary-700 hover:bg-primary-50 transition-all duration-300"
            >
              Ver Como Funciona
            </Button>
          </motion.div>

          {/* Value Propositions - Following Wireframe */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card variant="elegant" padding="lg" className="text-center space-y-5 group hover:shadow-premium transition-all duration-300 border border-primary-100/50 backdrop-blur-sm">
              <div className="w-18 h-18 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                <Lightbulb className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">Científico</h3>
              <p className="text-neutral-600 text-base leading-relaxed">Base em neurociência e psicologia aplicada</p>
            </Card>

            <Card variant="elegant" padding="lg" className="text-center space-y-5 group hover:shadow-premium transition-all duration-300 border border-secondary-100/50 backdrop-blur-sm">
              <div className="w-18 h-18 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-3xl flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                <Users className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">Personalizado</h3>
              <p className="text-neutral-600 text-base leading-relaxed">8 perfis específicos para mulheres empreendedoras</p>
            </Card>

            <Card variant="elegant" padding="lg" className="text-center space-y-5 group hover:shadow-premium transition-all duration-300 border border-neutral-200/50 backdrop-blur-sm">
              <div className="w-18 h-18 bg-gradient-to-br from-neutral-500 to-neutral-600 rounded-3xl flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">Prático</h3>
              <p className="text-neutral-600 text-base leading-relaxed">Plano de ação claro e implementável</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section id="como-funciona" className="py-28 bg-gradient-to-b from-neutral-50 via-white to-secondary-50/30 relative">
        {/* Enhanced background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-50/20 to-neutral-50/30"></div>
        <div className="absolute top-10 right-1/4 w-32 h-32 bg-gradient-to-br from-secondary-200/15 to-neutral-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-1/3 w-40 h-40 bg-gradient-to-br from-primary-200/10 to-secondary-200/15 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6 tracking-tight">
              Como <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Funciona</span>
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-primary-400 to-secondary-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-neutral-600 leading-relaxed font-light max-w-2xl mx-auto">
              Um processo simples e prático para descobrir seu perfil único de comunicação
            </p>
          </div>

          {/* Processo em 4 etapas */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            {[
              {
                icon: <Play className="w-8 h-8" />,
                step: "01",
                title: "Responda",
                description: "20 questões práticas em apenas " + getEstimatedTime() + " minutos",
                color: "primary"
              },
              {
                icon: <Target className="w-8 h-8" />,
                step: "02", 
                title: "Análise",
                description: "Algoritmo avalia 5 dimensões da comunicação",
                color: "secondary"
              },
              {
                icon: <FileText className="w-8 h-8" />,
                step: "03",
                title: "Resultado",
                description: "Descubra seu perfil entre 8 tipos únicos",
                color: "primary"
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                step: "04",
                title: "Desenvolva",
                description: "Plano personalizado de crescimento",
                color: "neutral"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: { 
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
                className="relative"
              >
                <Card className="text-center p-8 h-full border border-neutral-100/50 hover:shadow-elegant transition-all duration-300 group relative overflow-hidden flex flex-col">
                  {/* Step number */}
                  <div className="absolute top-4 right-4 text-5xl font-bold text-neutral-100 group-hover:text-neutral-200 transition-colors duration-300">
                    {item.step}
                  </div>
                  
                  <div className="flex flex-col items-center flex-grow">
                    <div className={`w-16 h-16 mb-6 rounded-3xl flex items-center justify-center shadow-elegant group-hover:scale-110 transition-transform duration-300 ${
                      item.color === 'primary' ? 'bg-gradient-to-br from-primary-500 to-primary-600' :
                      item.color === 'secondary' ? 'bg-gradient-to-br from-secondary-500 to-secondary-600' :
                      'bg-gradient-to-br from-neutral-500 to-neutral-600'
                    }`}>
                      <div className="text-white">
                        {item.icon}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed font-light text-center flex-grow flex items-center">
                      {item.description}
                    </p>
                  </div>
                </Card>
                
                {/* Connecting line for desktop */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary-300 to-secondary-300 transform -translate-y-1/2 z-10"></div>
                )}
              </motion.div>
            ))}
          </motion.div>

          {/* O que você receberá */}
          <motion.div 
            className="bg-gradient-to-br from-primary-50/30 to-secondary-50/20 rounded-3xl p-8 md:p-12 relative overflow-hidden max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Decorative elements */}
            <div className="absolute top-4 right-8 w-20 h-20 bg-gradient-to-br from-primary-200/20 to-secondary-200/20 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-neutral-900 mb-4 tracking-tight">
                  O que você receberá
                </h3>
                <p className="text-neutral-600 font-light max-w-2xl mx-auto">
                  Insights completos sobre seu perfil de comunicação
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
                <div className="space-y-8">
                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-elegant">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-neutral-900 mb-3 tracking-tight">Seu Perfil Detalhado</h4>
                      <p className="text-neutral-600 font-light leading-relaxed">Descubra suas características únicas de comunicação e como elas impactam seu negócio</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-elegant">
                      <Target className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-neutral-900 mb-3 tracking-tight">Pontos Fortes e Desafios</h4>
                      <p className="text-neutral-600 font-light leading-relaxed">Identifique onde você se destaca e áreas para desenvolvimento</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-neutral-500 to-neutral-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-elegant">
                      <TrendingUp className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-neutral-900 mb-3 tracking-tight">Estratégias Personalizadas</h4>
                      <p className="text-neutral-600 font-light leading-relaxed">Dicas práticas para potencializar sua comunicação e liderança</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-elegant">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-neutral-900 mb-3 tracking-tight">Plano de Desenvolvimento</h4>
                      <p className="text-neutral-600 font-light leading-relaxed">Roteiro claro para evolução contínua da sua comunicação</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mt-12">
                <Button 
                  variant="primary"
                  size="lg" 
                  onClick={handleStartAssessment}
                  className="text-lg px-16 py-5 rounded-2xl shadow-elegant hover:shadow-premium transition-all duration-300 transform hover:-translate-y-1 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700"
                >
                  Começar Minha Avaliação Agora
                </Button>
                <p className="text-sm text-neutral-500 mt-3 font-light">
                  ✨ Gratuito • Sem compromisso • Resultado imediato
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Seção de Perfis - Dinâmica */}
      <section className="py-28 bg-gradient-to-br from-primary-50/40 via-white to-neutral-50/30 relative border-t border-primary-100/50">
        {/* Enhanced decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/20 to-neutral-50/25"></div>
        <div className="absolute top-16 left-1/4 w-24 h-24 bg-gradient-to-br from-primary-200/20 to-secondary-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-16 right-1/3 w-32 h-32 bg-gradient-to-br from-secondary-200/15 to-neutral-200/20 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 right-10 w-3 h-3 bg-primary-300 rounded-full opacity-40"></div>
        <div className="absolute top-1/3 left-10 w-2 h-2 bg-secondary-400 rounded-full opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6 tracking-tight">
              CONHEÇA OS 8 PERFIS DE 
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent block">
                COMUNICAÇÃO FEMININA
              </span>
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-primary-400 to-secondary-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-neutral-600 leading-relaxed font-light">
              Cada perfil representa uma abordagem única de comunicação. Clique em qualquer card para conhecer mais detalhes.
            </p>
          </div>
          
          {loading && <p className="text-lg text-neutral-600">Carregando perfis...</p>}
          {error && <p className="text-lg text-red-500">Erro ao carregar perfis: {error}</p>}

          {!loading && !error && (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
            >
              {profiles.map((profile, index) => (
                <motion.div
                  key={profile.id || index}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { 
                      opacity: 1, 
                      y: 0,
                      transition: { 
                        duration: 0.6,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <Card 
                    variant="elegant"
                    hover={true}
                    className="text-center h-full flex flex-col group relative overflow-hidden border border-neutral-100/50 backdrop-blur-sm hover:border-primary-200/50 hover:shadow-premium transition-all duration-500"
                  >
                    {/* Subtle gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-secondary-50/0 group-hover:from-primary-50/20 group-hover:to-secondary-50/10 transition-all duration-500"></div>
                    
                    <div className="flex flex-col h-full space-y-5 p-7 relative z-10">
                      <div className="w-18 h-18 mx-auto rounded-3xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-elegant group-hover:scale-110 group-hover:shadow-premium transition-all duration-300">
                        <div className="text-white">
                          <ProfileIcon iconName={profile.icon} className="w-9 h-9" />
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold text-neutral-900 tracking-tight group-hover:text-primary-700 transition-colors duration-300">
                        {profile.name}
                      </h3>
                      <div className="flex-grow flex flex-col justify-center">
                        <p className="text-base text-neutral-600 leading-relaxed min-h-[4rem] font-light">
                          {profile.landing_description}
                        </p>
                      </div>
                      
                      {/* Subtle bottom accent */}
                      <div className="w-8 h-0.5 bg-gradient-to-r from-primary-400 to-secondary-400 mx-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>


      {/* Social Proof Section - DISABLED */}
      {/* 
      <section className="py-28 bg-gradient-to-br from-secondary-50/60 via-neutral-50/40 to-primary-50/30 relative overflow-hidden border-t border-secondary-100/60 disabled">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-50/30 to-primary-50/20"></div>
        <div className="absolute top-20 right-20 w-48 h-48 bg-gradient-to-br from-primary-200/20 to-secondary-200/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-40 h-40 bg-gradient-to-br from-secondary-200/25 to-neutral-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/2 w-56 h-56 bg-gradient-to-r from-neutral-100/15 to-secondary-100/20 rounded-full blur-3xl opacity-70"></div>
        
        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900 tracking-tight">
              Resultados <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Comprovados</span>
            </h2>
            <div className="w-12 h-1 bg-gradient-to-r from-primary-400 to-secondary-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-neutral-600 leading-relaxed font-light">
              Milhares de mulheres já transformaram sua comunicação e liderança
            </p>
          </div>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6, ease: "easeOut" }
                }
              }}
            >
              <Card variant="elegant" padding="lg" hover={true} className="text-center border-primary-100/50 backdrop-blur-sm group hover:shadow-premium transition-all duration-300 h-full flex flex-col">
                <div className="w-18 h-18 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-9 h-9 text-white" />
                </div>
                <div className="flex-grow flex flex-col justify-between">
                  <motion.div 
                    className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-3"
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 100 }}
                  >
                    1000+
                  </motion.div>
                  <div className="text-neutral-700 text-lg font-semibold tracking-tight">Mulheres Avaliadas</div>
                  <div className="text-neutral-500 text-base mt-2 font-light">Em todo o Brasil</div>
                </div>
              </Card>
            </motion.div>
            
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6, ease: "easeOut" }
                }
              }}
            >
              <Card variant="elegant" padding="lg" hover={true} className="text-center border-secondary-100/50 backdrop-blur-sm group hover:shadow-premium transition-all duration-300 h-full flex flex-col">
                <div className="w-18 h-18 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-9 h-9 text-white" />
                </div>
                <div className="flex-grow flex flex-col justify-between">
                  <motion.div 
                    className="text-4xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-3"
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.5, type: "spring", stiffness: 100 }}
                  >
                    94%
                  </motion.div>
                  <div className="text-neutral-700 text-lg font-semibold tracking-tight">Taxa de Satisfação</div>
                  <div className="text-neutral-500 text-base mt-2 font-light">Avaliam como excelente</div>
                </div>
              </Card>
            </motion.div>
            
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6, ease: "easeOut" }
                }
              }}
            >
              <Card variant="elegant" padding="lg" hover={true} className="text-center border-neutral-100/50 backdrop-blur-sm group hover:shadow-premium transition-all duration-300 h-full flex flex-col">
                <div className="w-18 h-18 bg-gradient-to-br from-neutral-500 to-neutral-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <Share2 className="w-9 h-9 text-white" />
                </div>
                <div className="flex-grow flex flex-col justify-between">
                  <motion.div 
                    className="text-4xl font-bold bg-gradient-to-r from-neutral-600 to-primary-600 bg-clip-text text-transparent mb-3"
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.7, type: "spring", stiffness: 100 }}
                  >
                    87%
                  </motion.div>
                  <div className="text-neutral-700 text-lg font-semibold tracking-tight">Recomendam</div>
                  <div className="text-neutral-500 text-base mt-2 font-light">Para outras empreendedoras</div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>
      */}

      {/* Final CTA */}
      {!isAuthenticated && (
        <section className="py-28 bg-gradient-to-br from-neutral-50/50 via-primary-50/40 to-secondary-50/40 relative overflow-hidden border-t border-neutral-200/50">
          {/* Enhanced decorative elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-50/20 to-primary-50/30"></div>
          <div className="absolute top-10 right-1/4 w-36 h-36 bg-gradient-to-br from-primary-200/20 to-neutral-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-1/3 w-28 h-28 bg-gradient-to-br from-secondary-200/25 to-primary-200/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-20 h-20 bg-gradient-to-br from-neutral-200/15 to-secondary-200/20 rounded-full blur-2xl opacity-60"></div>
          <div className="absolute top-1/4 right-10 w-4 h-4 bg-primary-300 rounded-full opacity-30"></div>
          <div className="absolute bottom-1/3 left-20 w-2 h-2 bg-secondary-400 rounded-full opacity-40"></div>
          
          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6 tracking-tight">
                Pronta para descobrir seu 
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent block">
                  potencial comunicativo?
                </span>
              </h2>
              <div className="w-12 h-1 bg-gradient-to-r from-primary-400 to-secondary-400 mx-auto mb-6 rounded-full"></div>
              <p className="text-xl text-neutral-600 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
                Inicie sua jornada de transformação comunicativa agora mesmo
              </p>
              <Button 
                variant="primary"
                size="lg" 
                onClick={handleStartAssessment}
                className="text-lg px-16 py-5 rounded-2xl shadow-elegant hover:shadow-premium transition-all duration-300 transform hover:-translate-y-1 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700"
              >
                Começar Agora - Grátis
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Botão Flutuante WhatsApp */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 2 }}
      >
        <motion.a
          href={createWhatsAppLink('home')}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </motion.a>
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Tire suas dúvidas pelo WhatsApp
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </motion.div>
    </div>
  );
};